import { useState, useEffect, useCallback } from 'react';
import type { UserProfileData, MembershipTier } from '../types/membership';
import { getUserProfile } from '../services/supabaseClient';

export type ReadingTheme = 'light' | 'dark' | 'sepia' | 'midnight' | 'forest' | 'ocean';

export interface UserSettings {
  displayName: string;
  avatarUrl: string;
  email: string;
  bio: string;
  readingGoal: number; // daily word goal
  notifications: {
    weeklyProgress: boolean;
    achievements: boolean;
    reminders: boolean;
  };
  preferences: {
    defaultWpm: number;
    fontSize: 'small' | 'medium' | 'large';
    fontFamily: string;
    theme: ReadingTheme;
  };
}

const STORAGE_KEY = 'flashread_user_profile';
const USER_ID_KEY = 'flashread_user_id';

const DEFAULT_SETTINGS: UserSettings = {
  displayName: 'Guest',
  avatarUrl: '',
  email: '',
  bio: '',
  readingGoal: 5000,
  notifications: {
    weeklyProgress: true,
    achievements: true,
    reminders: false
  },
  preferences: {
    defaultWpm: 200,
    fontSize: 'medium',
    fontFamily: 'system-ui',
    theme: 'light'
  }
};

export const useUserProfile = () => {
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS);
  const [isLoaded, setIsLoaded] = useState(false);
  const [membershipProfile, setMembershipProfile] = useState<UserProfileData | null>(null);
  const [isMembershipLoaded, setIsMembershipLoaded] = useState(false);

  // Load from localStorage on mount - BUT only if user is authenticated
  // This prevents old user data from appearing for new users
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Use getSession like App.tsx does - check if there's an active session
        let hasSession = false;
        let currentUserId = null;
        try {
          const { supabase } = await import('../services/supabaseClient');
          const { data: { session } } = await supabase.auth.getSession();
          hasSession = !!session;
          currentUserId = session?.user?.id || null;
        } catch (e) {
          // Supabase not available
        }
        
        const storedUserId = localStorage.getItem(USER_ID_KEY);
        const stored = localStorage.getItem(STORAGE_KEY);
        
        // If there's stored data and user IDs don't match, clear the data
        if (stored && storedUserId && currentUserId !== storedUserId) {
          console.log('Clearing old user data - user mismatch');
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(USER_ID_KEY);
          setSettings(DEFAULT_SETTINGS);
        } else if (hasSession && currentUserId) {
          // User is logged in and IDs match (or this is first load for this user) - load their settings
          if (stored) {
            const parsed = JSON.parse(stored);
            setSettings({ ...DEFAULT_SETTINGS, ...parsed });
          }
        } else {
          // No authenticated user - use defaults only
          console.log('No session - using default settings');
          setSettings(DEFAULT_SETTINGS);
        }
      } catch (error) {
        console.error('Failed to load user profile:', error);
      }
      setIsLoaded(true);
    };
    
    loadSettings();
  }, []);

  // Load membership profile from Supabase when user is authenticated
  // Validates user ID to prevent stale data from previous users
  useEffect(() => {
    const loadMembershipProfile = async () => {
      let currentUserId = null;
      
      try {
        const { supabase } = await import('../services/supabaseClient');
        // Use getSession() - cached, no network call
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          currentUserId = session.user.id;
        }
      } catch (e) {
        // Supabase not available
      }
      
      // Early return if no session - prevents any Supabase data queries for unauthenticated users
      if (!currentUserId) {
        setMembershipProfile(null);
        localStorage.removeItem(USER_ID_KEY);
        setIsMembershipLoaded(true);
        return;
      }
      
      // Get stored user ID from localStorage
      const storedUserId = localStorage.getItem(USER_ID_KEY);
      
      // If user changed (different user logged in), clear old user data
      if (currentUserId !== storedUserId) {
        if (storedUserId) {
          console.log('User changed, clearing user data. Old:', storedUserId, 'New:', currentUserId);
        }
        // Clear user ID - we'll set it when we load the profile
        localStorage.removeItem(USER_ID_KEY);
      }
      
      // Store current user ID and load profile
      localStorage.setItem(USER_ID_KEY, currentUserId);
      const profile = await getUserProfile(currentUserId);
      setMembershipProfile(profile);
      
      setIsMembershipLoaded(true);
    };

    loadMembershipProfile();
  }, []);

  // Save to localStorage whenever settings change
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      } catch (error) {
        console.error('Failed to save user profile:', error);
      }
    }
  }, [settings, isLoaded]);

  const updateSettings = useCallback((updates: Partial<UserSettings>): UserSettings => {
    setSettings(prev => ({ ...prev, ...updates }));
    return { ...settings, ...updates };
  }, [settings]);

  const updatePreferences = useCallback((updates: Partial<UserSettings['preferences']>): UserSettings['preferences'] => {
    const newPrefs = { ...settings.preferences, ...updates };
    setSettings(prev => ({ ...prev, preferences: newPrefs }));
    return newPrefs;
  }, [settings.preferences]);

  const updateNotifications = useCallback((updates: Partial<UserSettings['notifications']>): UserSettings['notifications'] => {
    const newNotifs = { ...settings.notifications, ...updates };
    setSettings(prev => ({ ...prev, notifications: newNotifs }));
    return newNotifs;
  }, [settings.notifications]);

  const setDisplayName = useCallback((name: string): void => {
    setSettings(prev => ({ ...prev, displayName: name }));
  }, []);

  const setAvatar = useCallback((avatarUrl: string): void => {
    setSettings(prev => ({ ...prev, avatarUrl }));
  }, []);

  const setBio = useCallback((bio: string): void => {
    setSettings(prev => ({ ...prev, bio }));
  }, []);

  const setReadingGoal = useCallback((goal: number): void => {
    setSettings(prev => ({ ...prev, readingGoal: goal }));
  }, []);

  const resetToDefaults = useCallback((): void => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  // Generate avatar from display name if no custom avatar
  const getAvatar = useCallback((): string => {
    if (settings.avatarUrl) return settings.avatarUrl;
    
    const initials = settings.displayName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    
    return initials || 'G';
  }, [settings.displayName, settings.avatarUrl]);

  // Get membership tier - returns 'guest' when not authenticated/verified
  const getMembershipTier = useCallback((): MembershipTier => {
    // If no profile loaded yet, return 'guest' (not verified)
    if (!membershipProfile) return 'guest';
    return (membershipProfile?.membership_tier as MembershipTier) || 'starter';
  }, [membershipProfile]);

  // Get total XP
  const getTotalXP = useCallback((): number => {
    return membershipProfile?.total_xp || 0;
  }, [membershipProfile]);

  // Get current level
  const getCurrentLevel = useCallback((): number => {
    return membershipProfile?.current_level || 1;
  }, [membershipProfile]);

  // Get current streak
  const getCurrentStreak = useCallback((): number => {
    return membershipProfile?.current_streak || 0;
  }, [membershipProfile]);

  return {
    settings,
    isLoaded,
    membershipProfile,
    isMembershipLoaded,
    updateSettings,
    updatePreferences,
    updateNotifications,
    setDisplayName,
    setAvatar,
    setBio,
    setReadingGoal,
    resetToDefaults,
    getAvatar,
    getMembershipTier,
    getTotalXP,
    getCurrentLevel,
    getCurrentStreak,
  };
};
